import React from 'react';

/*
  TermSummarySheet

  The fluid white-label Loan Term Summary. ONE renderer used everywhere a term
  summary appears: Review vs original (edit), View terms (readonly), and the
  borrower comparison (readonly, rendered two or three across).

  Fluid by design. The section frame is fixed (Loan pricing, Leverage, Funding,
  Structure, Fees, Dates, Estimated out-of-pocket), but the rows inside each
  section are built from whatever the AI pulled off this lender's term sheet.
  An empty section renders nothing. No two lenders' sheets look the same in
  content while staying identical in skin.

  PRIMARY DATA: ex.lineItems, an array of { label, value, section }. Every
  discrete labeled figure on the term sheet arrives here, tagged to a section,
  and renders as a line item. Long prose and legal language stays out of here
  (it lives in conditions and additionalTerms) so the catch-all stays short.

  If a document predates lineItems (older extraction), the renderer falls back
  to the named fields so nothing breaks.

  User corrections live in "overrides" and win per row. Out-of-pocket is
  override-driven: the lender's sheet rarely states the borrower's cash to
  close, so those lines are entered during Review vs original.

  Props:
    extracted   the AI extraction object (ex). May be null.
    overrides   { rows:{key:val}, oop:{...}, extraOop:[{label,amount}], extraRows:{section:[{label,value}]} }
    mode        'readonly' | 'edit'   (default 'readonly')
    meta        { lenderName, propertyAddress, preparedDate } optional header overrides
    onChange    (nextOverrides) called in edit mode on any edit
*/

const BRAND = {
  primary:  '#154D97',
  accent:   '#7EA3D7',
  mid:      '#3A6BA8',
  light:    '#D0E0F5',
  stripe:   '#EAF1FA',
  rule:     '#C5D5EA',
  textDark: '#0D2545',
  amber:    '#EF9F27',
  amberBg:  '#FAEEDA',
  amberTx:  '#854F0B',
};

const SECTIONS = [
  { key: 'pricing',   title: 'Loan pricing' },
  { key: 'leverage',  title: 'Leverage' },
  { key: 'funding',   title: 'Funding' },
  { key: 'structure', title: 'Structure' },
  { key: 'fees',      title: 'Fees' },
  { key: 'dates',     title: 'Dates' },
];

export default function TermSummarySheet({ extracted, overrides, mode = 'readonly', meta, onChange }) {
  const ex = extracted || {};
  const ov = normalizeOverrides(overrides);
  const editing = mode === 'edit';

  const lenderName = (meta && meta.lenderName) || ex.lenderName || 'Lender';
  const propertyAddress = (meta && meta.propertyAddress) || ex.propertyAddress || '';
  const preparedDate = (meta && meta.preparedDate) || todayStamp();
  const loanType = ex.loanType || '';

  const items = Array.isArray(ex.lineItems) ? ex.lineItems : [];
  const hasItems = items.length > 0;
  const basisNote = ex.leverage && ex.leverage.basisVerbatim ? ex.leverage.basisVerbatim : '';
  const catchAll = buildCatchAll(ex);

  // Build the rows for a section. Prefer lineItems; fall back to named fields
  // for documents extracted before lineItems existed.
  const sectionRows = (key) => {
    if (hasItems) {
      return items
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => normSection(it.section) === key)
        .map(({ it, i }) => ({
          key: `li:${key}:${i}`,
          label: it.label || '',
          value: it.value == null ? '' : String(it.value),
          emphasis: /loan amount/i.test(it.label || ''),
        }));
    }
    if (key === 'pricing') return buildPricingRows(ex);
    if (key === 'leverage') return buildLeverageRows(ex);
    if (key === 'structure') return buildStructureRows(ex);
    return [];
  };

  const rowVal = (key, derived) => (ov.rows[key] != null ? ov.rows[key] : derived);

  const setRow = (key, value) => {
    if (!onChange) return;
    onChange({ ...ov, rows: { ...ov.rows, [key]: value } });
  };
  const setLabel = (key, value) => {
    if (!onChange) return;
    onChange({ ...ov, labels: { ...ov.labels, [key]: value } });
  };
  const hideRow = (key) => {
    if (!onChange) return;
    onChange({ ...ov, hidden: { ...ov.hidden, [key]: true } });
  };
  const setOop = (key, value) => {
    if (!onChange) return;
    onChange({ ...ov, oop: { ...ov.oop, [key]: value } });
  };
  const setExtraOop = (i, patch) => {
    if (!onChange) return;
    const next = ov.extraOop.slice();
    next[i] = { ...next[i], ...patch };
    onChange({ ...ov, extraOop: next });
  };
  const addExtraOop = () => {
    if (!onChange) return;
    onChange({ ...ov, extraOop: [...ov.extraOop, { label: '', amount: '' }] });
  };
  const calcOop = () => {
    if (!onChange) return;
    const ai = ex.outOfPocket || {};
    const hasAi = ai.downPayment != null || ai.proceeds != null || ai.lenderFees != null || ai.brokerFee != null || ai.thirdPartyCosts != null;
    let c;
    if (hasAi) {
      c = {
        downPayment:  ai.downPayment != null ? String(ai.downPayment) : '',
        proceeds:     ai.proceeds != null ? String(ai.proceeds) : (ai.credits != null ? String(ai.credits) : ''),
        lenderFees:   ai.lenderFees != null ? String(ai.lenderFees) : '',
        brokerFee:    ai.brokerFee != null ? String(ai.brokerFee) : '',
        closingCosts: ai.thirdPartyCosts != null ? String(ai.thirdPartyCosts) : '',
      };
    } else {
      c = calcFromTerms(ex);
      c.proceeds = '';
      c.closingCosts = '';
    }
    const nextOop = { ...ov.oop };
    if (c.downPayment !== '') nextOop.downPayment = c.downPayment;
    if (c.proceeds !== '') nextOop.proceeds = c.proceeds;
    if (c.lenderFees !== '') nextOop.lenderFees = c.lenderFees;
    if (c.brokerFee !== '') nextOop.brokerFee = c.brokerFee;
    if (c.closingCosts !== '') nextOop.closingCosts = c.closingCosts;
    onChange({ ...ov, oop: nextOop });
  };
  const removeExtraOop = (i) => {
    if (!onChange) return;
    const next = ov.extraOop.slice();
    next.splice(i, 1);
    onChange({ ...ov, extraOop: next });
  };
  const extraRowsFor = (key) => (Array.isArray(ov.extraRows[key]) ? ov.extraRows[key] : []);
  const setExtraRow = (key, i, patch) => {
    if (!onChange) return;
    const list = extraRowsFor(key).slice();
    list[i] = { ...list[i], ...patch };
    onChange({ ...ov, extraRows: { ...ov.extraRows, [key]: list } });
  };
  const addExtraRow = (key) => {
    if (!onChange) return;
    const list = extraRowsFor(key).slice();
    list.push({ label: '', value: '' });
    onChange({ ...ov, extraRows: { ...ov.extraRows, [key]: list } });
  };
  const removeExtraRow = (key, i) => {
    if (!onChange) return;
    const list = extraRowsFor(key).slice();
    list.splice(i, 1);
    onChange({ ...ov, extraRows: { ...ov.extraRows, [key]: list } });
  };

  const aiOop = ex.outOfPocket || {};
  const purposeRaw = String(aiOop.purpose || ex.loanType || '').toLowerCase();
  const isRefi = /refi|refinance|cash.?out/.test(purposeRaw);
  const oopDefaults = {
    downPayment:  aiOop.downPayment != null ? String(aiOop.downPayment) : '',
    lenderFees:   aiOop.lenderFees != null ? String(aiOop.lenderFees) : (ex.originationFees != null ? String(ex.originationFees) : ''),
    brokerFee:    aiOop.brokerFee != null ? String(aiOop.brokerFee) : '',
    closingCosts: aiOop.thirdPartyCosts != null ? String(aiOop.thirdPartyCosts) : '',
    proceeds:     aiOop.proceeds != null ? String(aiOop.proceeds) : (aiOop.credits != null ? String(aiOop.credits) : ''),
  };
  const oopNotes = aiOop.notes ? String(aiOop.notes) : '';
  const oopVal = (key) => {
    if (ov.hidden[key]) return '';
    if (ov.oop[key] != null && ov.oop[key] !== '') return ov.oop[key];
    if (ov.oop[key] === '') return editing ? '' : '0';
    const def = oopDefaults[key];
    if (def != null && def !== '') return def;
    return editing ? '' : '0';
  };
  // Out-of-pocket is the cash the borrower actually pays from their own funds.
  // Purchase has a down payment; a refinance does not (payoffs are financed and
  // live in Funding), but a refinance still has real out-of-pocket costs that
  // were not rolled into the loan. So both modes sum cost lines into cash to
  // close. A refinance ALSO shows proceeds to the borrower as a separate figure.
  const oopLines = isRefi
    ? [
        { key: 'lenderFees',   label: 'Lender fees' },
        { key: 'brokerFee',    label: 'Crafted Capital broker fee' },
        { key: 'closingCosts', label: 'Title, escrow, insurance, misc' },
      ]
    : [
        { key: 'downPayment',  label: 'Down payment' },
        { key: 'lenderFees',   label: 'Lender fees' },
        { key: 'brokerFee',    label: 'Crafted Capital broker fee' },
        { key: 'closingCosts', label: 'Title, escrow, insurance, misc' },
      ];

  const oopValNum = (key) => {
    if (ov.hidden[key]) return 0;
    let raw = ov.oop[key];
    if (raw == null) raw = oopDefaults[key];
    return num(raw);
  };
  let extraOopSum = 0;
  ov.extraOop.forEach((e) => { extraOopSum += num(e.amount); });
  let barValueNum = extraOopSum;
  oopLines.forEach((l) => { barValueNum += oopValNum(l.key); });
  const barLabel = 'Estimated cash to close';
  const showOop = true;

  return (
    <div style={t.sheet}>

      <div style={t.header}>
        <div style={t.brandWrap}>
          <div style={t.logo}>CC</div>
          <div>
            <div style={t.brandName}>Crafted Capital Co.</div>
            <div style={t.brandTag}>PRIVATE LENDING SOLUTIONS</div>
          </div>
        </div>
        <div style={t.headRight}>
          <div style={t.docTitle}>Loan Term Summary</div>
          {loanType ? <div style={t.docSub}>{loanType}</div> : null}
        </div>
      </div>

      <div style={t.metaRow}>
        <span>{propertyAddress || (editing ? 'Property address' : '')}</span>
        <span>Prepared {preparedDate}</span>
      </div>

      {SECTIONS.map((sec) => {
        const rows = sectionRows(sec.key);
        const extra = extraRowsFor(sec.key);
        if (rows.length === 0 && extra.length === 0 && !editing) return null;
        if (rows.length === 0 && extra.length === 0 && editing && sec.key !== 'pricing') {
          // In edit mode, only show an empty section if it is pricing (always
          // useful) or once the user adds to it. Skip noise.
          return null;
        }
        return (
          <Section key={sec.key} title={sec.title}>
            {rows.filter((r) => !ov.hidden[r.key]).map((r) => (
              <Row
                key={r.key}
                label={ov.labels[r.key] != null ? ov.labels[r.key] : r.label}
                value={rowVal(r.key, r.value)}
                emphasis={r.emphasis}
                io={hasIo(rowVal(r.key, r.value))}
                flagged={r.flagged}
                editing={editing}
                editableLabel
                onLabel={(v) => setLabel(r.key, v)}
                onChange={(v) => setRow(r.key, v)}
                onRemove={() => hideRow(r.key)}
              />
            ))}
            {extra.map((e, i) => (
              <Row
                key={`ex:${sec.key}:${i}`}
                label={e.label}
                value={e.value}
                editing={editing}
                editableLabel
                onLabel={(v) => setExtraRow(sec.key, i, { label: v })}
                onChange={(v) => setExtraRow(sec.key, i, { value: v })}
                onRemove={() => removeExtraRow(sec.key, i)}
              />
            ))}
            {sec.key === 'leverage' && basisNote ? <div style={t.basisNote}>As written: {basisNote}</div> : null}
            {editing && (
              <button type="button" style={t.addBtn} onClick={() => addExtraRow(sec.key)}>
                <i className="ti ti-plus" style={{ fontSize: '13px', verticalAlign: '-2px' }} /> Add line
              </button>
            )}
          </Section>
        );
      })}

      {showOop && (
        <Section title="Estimated out-of-pocket">
          {editing && (
            <button type="button" style={t.calcBtn} onClick={calcOop}>
              <i className="ti ti-calculator" style={{ fontSize: '13px', verticalAlign: '-2px' }} /> Calculate from terms above
            </button>
          )}
          {editing && oopNotes ? <div style={t.oopNote}><i className="ti ti-info-circle" style={{ fontSize: '12px', verticalAlign: '-1px', marginRight: '4px' }} />{oopNotes}</div> : null}
          {oopLines.filter((l) => !ov.hidden[l.key]).map((l) => (
            <Row
              key={l.key}
              label={ov.labels[l.key] != null ? ov.labels[l.key] : l.label}
              value={oopVal(l.key)}
              numeric
              editing={editing}
              editableLabel
              onLabel={(v) => setLabel(l.key, v)}
              onChange={(v) => setOop(l.key, v)}
              onRemove={() => hideRow(l.key)}
            />
          ))}
          {ov.extraOop.map((e, i) => (
            <Row
              key={`xo:${i}`}
              label={e.label}
              value={e.amount}
              numeric
              editing={editing}
              editableLabel
              onLabel={(v) => setExtraOop(i, { label: v })}
              onChange={(v) => setExtraOop(i, { amount: v })}
              onRemove={() => removeExtraOop(i)}
            />
          ))}
          {editing && (
            <button type="button" style={t.addBtn} onClick={addExtraOop}>
              <i className="ti ti-plus" style={{ fontSize: '13px', verticalAlign: '-2px' }} /> Add cost line (counts toward total)
            </button>
          )}
          {isRefi && !ov.hidden.proceeds && (
            <Row
              label={ov.labels.proceeds != null ? ov.labels.proceeds : 'Estimated proceeds to borrower'}
              value={oopVal('proceeds')}
              numeric
              editing={editing}
              editableLabel
              onLabel={(v) => setLabel('proceeds', v)}
              onChange={(v) => setOop('proceeds', v)}
              onRemove={() => hideRow('proceeds')}
            />
          )}
          {isRefi && editing && (
            <div style={t.oopNote}>
              <i className="ti ti-info-circle" style={{ fontSize: '12px', verticalAlign: '-1px', marginRight: '4px' }} />
              Cash to close is what the borrower pays from their own funds. Proceeds to borrower is separate money they receive from the loan. A refi can have both.
            </div>
          )}
        </Section>
      )}

      {showOop && (
        <div style={t.ctcBar}>
          <span style={t.ctcLabel}>{barLabel}</span>
          <span style={t.ctcValue}>{money(barValueNum)}</span>
        </div>
      )}

      {showOop && isRefi && !ov.hidden.proceeds && num(oopVal('proceeds')) > 0 && (
        <div style={t.proceedsBar}>
          <span style={t.ctcLabel}>Estimated proceeds to borrower</span>
          <span style={t.ctcValue}>{money(num(oopVal('proceeds')))}</span>
        </div>
      )}

      {catchAll.length > 0 && (
        <Section title="Conditions and additional terms">
          <ul style={t.ul}>
            {catchAll.map((c, i) => <li key={i} style={t.li}>{c}</li>)}
          </ul>
        </Section>
      )}

      <div style={t.footer}>
        <span style={t.footL}>Indicative summary. Not a commitment to lend. Third-party costs finalized at settlement.</span>
        <span style={t.footR}>craftedcapitalco.com</span>
      </div>

    </div>
  );
}

/* ---------- named-field fallback builders (pre-lineItems docs) ---------- */

function buildPricingRows(ex) {
  const rows = [];
  if (ex.loanAmount != null) rows.push({ key: 'loanAmount', label: 'Loan amount', value: money(ex.loanAmount), emphasis: true });
  const rate = composeRate(ex);
  if (rate.text) rows.push({ key: 'rate', label: 'Interest rate', value: rate.text });
  if (ex.points != null) rows.push({ key: 'origination', label: 'Origination', value: `${ex.points} points` });
  if (ex.termMonths != null) rows.push({ key: 'term', label: 'Term', value: `${ex.termMonths} months` });
  return rows;
}

function composeRate(ex) {
  const r = ex.interestRate || {};
  if (r.ratePct == null && !ex.amortization) return { text: '' };
  const parts = [];
  if (r.ratePct != null) parts.push(`${r.ratePct}%`);
  if (r.rateType) parts.push(String(r.rateType).toLowerCase());
  let text = parts.join(' ');
  const io = !!(ex.amortization && /interest.?only|^io$/i.test(String(ex.amortization)));
  if (io) text = text ? `${text}, IO` : 'IO';
  if (r.indexAndMargin) text = text ? `${text} (${r.indexAndMargin})` : r.indexAndMargin;
  return { text };
}

function buildLeverageRows(ex) {
  const lv = ex.leverage || {};
  const rows = [];
  if (lv.maxLtcPct != null) rows.push({ key: 'ltc', label: 'Max LTC', value: `${lv.maxLtcPct}%` });
  if (lv.maxLtvPct != null) rows.push({ key: 'ltv', label: 'Max LTV', value: `${lv.maxLtvPct}%` });
  if (lv.maxLtarvPct != null) rows.push({ key: 'arltv', label: 'Max ARLTV', value: `${lv.maxLtarvPct}%` });
  if (rows.length === 0 && lv.basisVerbatim) rows.push({ key: 'basis', label: 'Leverage', value: lv.basisVerbatim });
  return rows;
}

function buildStructureRows(ex) {
  const rows = [];
  if (ex.recourse) rows.push({ key: 'recourse', label: 'Recourse', value: ex.recourse });
  if (ex.personalGuaranty != null) rows.push({ key: 'pg', label: 'Personal guaranty', value: ex.personalGuaranty ? 'Required' : 'Not required' });
  if (ex.prepaymentPenalty) rows.push({ key: 'prepay', label: 'Prepayment penalty', value: ex.prepaymentPenalty });
  if (ex.amortization && !/interest.?only|^io$/i.test(String(ex.amortization))) rows.push({ key: 'amort', label: 'Amortization', value: ex.amortization });
  if (ex.reservesRequired) rows.push({ key: 'reserves', label: 'Reserves', value: ex.reservesRequired });
  if (ex.appraisalRequirement) rows.push({ key: 'appraisal', label: 'Appraisal', value: ex.appraisalRequirement });
  return rows;
}

function buildCatchAll(ex) {
  const out = [];
  if (Array.isArray(ex.conditions)) ex.conditions.forEach((c) => { if (c) out.push(c); });
  if (Array.isArray(ex.additionalTerms)) ex.additionalTerms.forEach((c) => { if (c) out.push(c); });
  return out;
}

/* ---------- presentational pieces ---------- */

function Section({ title, children }) {
  return (
    <div style={t.section}>
      <div style={t.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, emphasis, io, flagged, numeric, editing, editableLabel, onLabel, onChange, onRemove }) {
  return (
    <div style={t.row}>
      {editing && editableLabel ? (
        <input value={label || ''} onChange={(e) => onLabel && onLabel(e.target.value)} placeholder="Label" style={t.labelInput} />
      ) : (
        <span style={t.rowLabel}>{flagged ? <span style={t.flagDot} /> : null}{label}</span>
      )}
      {editing ? (
        <span style={t.editRight}>
          <input
            value={value == null ? '' : value}
            onChange={(e) => onChange && onChange(e.target.value)}
            placeholder={numeric ? '0' : ''}
            inputMode={numeric ? 'numeric' : undefined}
            style={{ ...t.input, ...(flagged ? t.inputFlagged : null) }}
          />
          {onRemove ? (
            <button type="button" onClick={onRemove} style={t.removeBtn} aria-label="Remove line"><i className="ti ti-x" style={{ fontSize: '13px' }} /></button>
          ) : null}
        </span>
      ) : (
        <span style={emphasis ? t.rowValEmph : t.rowVal}>{numeric ? money(value) : (io ? withIo(value) : value)}</span>
      )}
    </div>
  );
}

function withIo(text) {
  const str = String(text == null ? '' : text);
  const idx = str.indexOf('IO');
  if (idx === -1) return str;
  return (
    <>
      {str.slice(0, idx)}
      <span style={t.ioTag}>IO</span>
      {str.slice(idx + 2)}
    </>
  );
}

/* ---------- helpers ---------- */

function normSection(s) {
  const v = String(s || '').toLowerCase();
  if (SECTIONS.some((x) => x.key === v)) return v;
  return 'structure';
}

function hasIo(v) {
  return /(^|[^a-z])io([^a-z]|$)/i.test(String(v == null ? '' : v));
}

function normalizeOverrides(o) {
  const base = { rows: {}, labels: {}, hidden: {}, oop: {}, extraOop: [], extraRows: {} };
  if (!o || typeof o !== 'object') return base;
  return {
    rows: o.rows && typeof o.rows === 'object' ? { ...o.rows } : {},
    labels: o.labels && typeof o.labels === 'object' ? { ...o.labels } : {},
    hidden: o.hidden && typeof o.hidden === 'object' ? { ...o.hidden } : {},
    oop: o.oop && typeof o.oop === 'object' ? { ...o.oop } : {},
    extraOop: Array.isArray(o.extraOop) ? o.extraOop.map((e) => ({ label: e.label || '', amount: e.amount == null ? '' : e.amount })) : [],
    extraRows: o.extraRows && typeof o.extraRows === 'object' ? { ...o.extraRows } : {},
  };
}

function num(v) {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return isNaN(n) ? 0 : n;
}

// One-time math from the extracted terms. Reads figures already on the sheet
// and returns starting out-of-pocket lines. This never runs on its own; it is
// only called when the user presses Calculate, and the user reviews and
// corrects the results afterward.
function calcFromTerms(ex) {
  const e = ex || {};
  const loan = num(e.loanAmount);
  const purchase = num(findLineValue(e, /purchase price/i));
  const initialFunding = num(findLineValue(e, /initial (loan )?funding|initial advance/i));

  // Down payment: purchase price less the day-one loan funding toward it.
  // Falls back to the full loan amount when no initial funding is stated.
  let downPayment = '';
  if (purchase > 0) {
    const fundingTowardPurchase = initialFunding > 0 ? initialFunding : loan;
    const dp = purchase - fundingTowardPurchase;
    downPayment = dp > 0 ? String(Math.round(dp)) : '0';
  }

  // Lender fees: origination dollars + processing + onboarding.
  let origination = 0;
  if (e.originationFees != null && num(e.originationFees) > 0) {
    origination = num(e.originationFees);
  } else {
    const origRaw = findLineValue(e, /origination/i);
    const pct = pctOf(origRaw, loan);
    origination = pct != null ? pct : (dollarOf(origRaw) || 0);
  }
  const processing = dollarOf(findLineValue(e, /processing/i)) || 0;
  const onboarding = dollarOf(findLineValue(e, /onboard/i)) || 0;
  const lenderFeesNum = origination + processing + onboarding;
  const lenderFees = lenderFeesNum > 0 ? String(Math.round(lenderFeesNum)) : '';

  // Broker fee: third party brokerage, a percent of the loan or flat dollars.
  let brokerFee = '';
  const brokerRaw = findLineValue(e, /broker/i);
  if (brokerRaw) {
    const pct = pctOf(brokerRaw, loan);
    const dol = dollarOf(brokerRaw);
    const bf = pct != null ? pct : (dol || 0);
    brokerFee = bf > 0 ? String(Math.round(bf)) : '';
  }

  return { downPayment, lenderFees, brokerFee };
}

function findLineValue(ex, regex) {
  const items = Array.isArray(ex.lineItems) ? ex.lineItems : [];
  for (const it of items) {
    if (it && regex.test(String(it.label || ''))) return String(it.value == null ? '' : it.value);
  }
  return '';
}

// Returns a percent of base if the value is written as a percent, else null.
function pctOf(value, base) {
  if (!/%/.test(String(value))) return null;
  const p = num(value);
  return p > 0 && base > 0 ? (p / 100) * base : null;
}

// Returns a dollar figure if the value reads as dollars, else null. A pure
// percent (no dollar sign) returns null so it is not mistaken for dollars.
function dollarOf(value) {
  const s = String(value == null ? '' : value);
  if (!s) return null;
  if (/%/.test(s) && !/\$/.test(s)) return null;
  const n = num(s);
  return n > 0 ? n : null;
}

function money(v) {
  if (v == null || v === '') return '-';
  const n = num(v);
  return '$' + Math.round(n).toLocaleString('en-US');
}

function todayStamp() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}.${dd}.${d.getFullYear()}`;
}

/* ---------- styles (brand exact) ---------- */

const t = {
  sheet:      { background: '#fff', borderRadius: '10px', overflow: 'hidden', fontFamily: 'Helvetica, Arial, sans-serif', color: BRAND.textDark, border: `0.5px solid ${BRAND.rule}` },

  header:     { background: BRAND.primary, padding: '16px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brandWrap:  { display: 'flex', alignItems: 'center', gap: '10px' },
  logo:       { width: '34px', height: '34px', background: '#fff', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND.primary, fontWeight: 700, fontSize: '14px' },
  brandName:  { color: '#fff', fontSize: '14px', fontWeight: 700 },
  brandTag:   { color: BRAND.accent, fontSize: '9px', letterSpacing: '0.8px' },
  headRight:  { textAlign: 'right' },
  docTitle:   { color: '#fff', fontSize: '13px', fontWeight: 700 },
  docSub:     { color: BRAND.accent, fontSize: '10px' },

  metaRow:    { padding: '11px 22px', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: BRAND.mid, borderBottom: `1px solid ${BRAND.stripe}` },

  section:      { padding: '10px 22px 4px' },
  sectionTitle: { fontSize: '11px', color: BRAND.mid, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', borderBottom: `1px solid ${BRAND.rule}`, paddingBottom: '5px', marginBottom: '2px' },

  row:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '6px 0', borderBottom: `1px solid ${BRAND.stripe}`, minHeight: '34px' },
  rowLabel:   { color: BRAND.mid, fontSize: '12px', flex: 1 },
  rowVal:     { fontSize: '12.5px', fontWeight: 700, color: BRAND.textDark, textAlign: 'right' },
  rowValEmph: { fontSize: '15px', fontWeight: 700, color: BRAND.primary, textAlign: 'right' },
  flagDot:    { display: 'inline-block', width: '7px', height: '7px', background: BRAND.amber, borderRadius: '50%', marginRight: '6px', verticalAlign: 'middle' },
  ioTag:      { color: BRAND.amberTx, background: BRAND.amberBg, fontWeight: 700, padding: '0 5px', borderRadius: '3px', marginLeft: '2px' },
  basisNote:  { fontSize: '11px', color: BRAND.mid, fontStyle: 'italic', marginTop: '4px' },

  editRight:    { display: 'flex', alignItems: 'center', gap: '6px' },
  input:        { width: '170px', textAlign: 'right', fontSize: '12px', height: '30px', padding: '2px 8px', border: `1px solid ${BRAND.rule}`, borderRadius: '5px', fontFamily: 'inherit', color: BRAND.textDark },
  inputFlagged: { border: `1px solid ${BRAND.amber}`, background: BRAND.amberBg },
  labelInput:   { flex: 1, marginRight: '8px', fontSize: '12px', height: '30px', padding: '2px 8px', border: `1px solid ${BRAND.rule}`, borderRadius: '5px', fontFamily: 'inherit', color: BRAND.mid },
  removeBtn:    { background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', padding: '2px', lineHeight: 1 },
  addBtn:       { marginTop: '6px', marginBottom: '4px', fontSize: '11px', padding: '4px 10px', background: 'transparent', color: BRAND.primary, border: `1px solid ${BRAND.rule}`, borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 },
  calcBtn:      { display: 'block', width: '100%', marginBottom: '8px', fontSize: '11.5px', padding: '7px 10px', background: BRAND.stripe, color: BRAND.primary, border: `1px solid ${BRAND.accent}`, borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 },
  oopNote:      { fontSize: '11px', color: BRAND.mid, background: BRAND.stripe, borderRadius: '6px', padding: '7px 9px', marginBottom: '8px', lineHeight: 1.5 },

  ctcBar:     { margin: '8px 22px 14px', background: BRAND.primary, borderRadius: '6px', padding: '13px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  proceedsBar: { margin: '-6px 22px 14px', background: BRAND.mid, borderRadius: '6px', padding: '13px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  ctcLabel:   { color: '#fff', fontSize: '13px' },
  ctcValue:   { color: '#fff', fontSize: '22px', fontWeight: 700 },

  ul:         { margin: '6px 0 4px', paddingLeft: '18px' },
  li:         { fontSize: '12.5px', color: BRAND.textDark, marginBottom: '4px', lineHeight: 1.45 },

  footer:     { background: BRAND.primary, padding: '10px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' },
  footL:      { color: BRAND.accent, fontSize: '9px' },
  footR:      { color: '#fff', fontSize: '9px' },
};
